import React, { useEffect, useState, useCallback, useMemo } from 'react';
import './LabPricingStyles.css';
import axios from "axios";
import { APIURL, TZ } from "../../../Global";
import { useAlert } from 'react-alert';
import { fetchTestGroups } from './sampleList';
import PanelShow from './PanelShow';
import TestShow from './TestShow';
import TestDetails from './TestDetails';
import { FaPlus } from "react-icons/fa";

const TestGroup = ({ search, refresh }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [showTestGroupDetails, setShowTestGroupDetails] = useState(false);
  const [data, setData] = useState(null);
  const [allData, setAllData] = useState(null);
  const [searchData, setSearchData] = useState(null);
  const alert = useAlert();
  const [previousSelectedTestId, setPreviousSelectedTestId] = useState(null);
  const [previousSelectedPanelId, setPreviousSelectedPanelId] = useState(null);
  const [testGroups, setTestGroups] = useState([]);
  const [isMounted, setIsMounted] = useState(true);
  const [addPanel, setAddPanel] = useState(false);
  const [addTest, setAddTest] = useState(false);

  // Get auth token
  const getAuthToken = useCallback(() => {
    const tokenString = sessionStorage.getItem('usertoken');
    return tokenString ? tokenString.replace(/["]+/g, '') : null;
  }, []);

  // Debounced search function
  const debounce = useCallback((func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  // Search API call function
  const handleSearchAPI = useCallback((searchValue) => {
    if (!searchValue || searchValue.trim() === '') {
      setSearchData(null);
      return;
    }

    const token = getAuthToken();
    if (!token) return;

    axios.get(`${APIURL}/api/v1/staff/master-test-group/?search=${encodeURIComponent(searchValue)}&${TZ}`, {
      headers: {
        "Authorization": 'Token ' + token
      }
    })
      .then(res => {
        if (isMounted && res.status === 200) {
          if (res.data.status === 'success') {
            setSearchData(res.data);
          } else {
            alert.error(res.data.message);
            setSearchData(null);
          }
        }
      })
      .catch(err => {
        if (isMounted) {
          alert.error('Error fetching search data');
          setSearchData(null);
        }
      });
  }, [isMounted, alert, getAuthToken]);

  // Debounced search with 500ms delay
  const debouncedSearch = useMemo(
    () => debounce(handleSearchAPI, 500),
    [handleSearchAPI, debounce]
  );

  // Effect for handling search changes
  useEffect(() => {
    if (search) {
      debouncedSearch(search);
    } else {
      setSearchData(null);
      if (selectedCategory && selectedCategory.value) {
        handleTestGroupData(selectedCategory);
      }
    }
  }, [search, debouncedSearch]);

  // Load test groups on mount and refresh
  useEffect(() => {
    setIsMounted(true);

    const loadTestGroups = async () => {
      try {
        const groups = await fetchTestGroups();
        if (isMounted) {
          setTestGroups(groups);
          if (groups.length > 0 && !selectedCategory && !search) {
            setSelectedCategory(groups[0]);
            handleTestGroupData(groups[0]);
          }
        }
      } catch (error) {
        console.error('Error loading test groups:', error);
      }
    };

    loadTestGroups();

    return () => {
      setIsMounted(false);
    };
  }, [isMounted, refresh]);

  const handleTestGroupData = useCallback((testgroup) => {
    const token = getAuthToken();
    if (!token) return;

    axios.get(`${APIURL}/api/v1/staff/master-test-group/?group_test_id=${testgroup.value}&${TZ}`, {
      headers: {
        "Authorization": 'Token ' + token
      }
    })
      .then(res => {
        if (isMounted && res.status === 200) {
          if (res.data.status === 'success') {
            setData(res.data);

            // Only restore previous selections if they exist in the new data
            if (previousSelectedTestId) {
              const updatedSelectedTest = res.data.tests?.find(test => test.id === previousSelectedTestId);
              setSelectedTest(updatedSelectedTest || null);
              if (!updatedSelectedTest) {
                setPreviousSelectedTestId(null);
              }
            }
            if (previousSelectedPanelId) {
              const updatedSelectedPanel = res.data.panels?.find(panel => panel.id === previousSelectedPanelId);
              setSelectedPanel(updatedSelectedPanel || null);
              if (!updatedSelectedPanel) {
                setPreviousSelectedPanelId(null);
              }
            }
          } else {
            alert.error(res.data.message);
            setData(null);
          }
        }
      })
      .catch(err => {
        if (isMounted) {
          alert.error('Error fetching sample data');
        }
      });
  }, [isMounted, alert, getAuthToken, previousSelectedTestId, previousSelectedPanelId]);

  // Get unique groups from search results with their data
  const getGroupsWithSearchData = useMemo(() => {
    if (!searchData) return {};

    const groupData = {};

    // Group tests by group_name
    searchData.tests?.forEach(test => {
      if (test.group_name) {
        if (!groupData[test.group_name]) {
          groupData[test.group_name] = { tests: [], panels: [] };
        }
        groupData[test.group_name].tests.push(test);
      }
    });

    // Group panels by group_name
    searchData.panels?.forEach(panel => {
      if (panel.group_name) {
        if (!groupData[panel.group_name]) {
          groupData[panel.group_name] = { tests: [], panels: [] };
        }
        groupData[panel.group_name].panels.push(panel);
      }
    });

    return groupData;
  }, [searchData]);

  // Determine which test groups to display
  const displayedTestGroups = useMemo(() => {
    if (search && searchData) {
      const groupNames = Object.keys(getGroupsWithSearchData);
      return testGroups.filter(group => groupNames.includes(group.label));
    }
    return testGroups;
  }, [search, searchData, getGroupsWithSearchData, testGroups]);

  // Auto-select first group with search results
  useEffect(() => {
    if (search && searchData && displayedTestGroups.length > 0) {
      const currentGroupHasData = selectedCategory && getGroupsWithSearchData[selectedCategory.label];

      if (!currentGroupHasData ||
        (currentGroupHasData.tests.length === 0 && currentGroupHasData.panels.length === 0)) {
        // Find first group with data
        const firstGroupWithData = displayedTestGroups.find(group => {
          const groupData = getGroupsWithSearchData[group.label];
          return groupData && (groupData.tests.length > 0 || groupData.panels.length > 0);
        });

        if (firstGroupWithData) {
          setSelectedCategory(firstGroupWithData);
        }
      }
    }
  }, [search, searchData, displayedTestGroups, getGroupsWithSearchData, selectedCategory]);

  useEffect(() => {
    handleAllData()
  }, [])

  const handleCategoryClick = useCallback((category) => {
    setSelectedCategory(category);
    // Only call API if not searching
    if (!search) {
      handleTestGroupData(category);
    }
    handleAllData();

    // Reset selections and show default details state when changing test group
    setSelectedTest(null);
    setSelectedPanel(null);
    setShowTestGroupDetails(false);
    setPreviousSelectedTestId(null);
    setPreviousSelectedPanelId(null);
  }, [search, handleTestGroupData]);

  // New function to handle test group header click
  const handleTestGroupHeaderClick = useCallback(() => {
    setSelectedTest(null);
    setSelectedPanel(null);
    setShowTestGroupDetails(true);
  }, []);

  const handleItemClick = useCallback((item, type) => {
    setShowTestGroupDetails(false);

    if (type === "test") {
      if (selectedTest !== item) {
        handleAllData();
        setSelectedTest(item);
        setPreviousSelectedTestId(item.id);
      } else {
        setSelectedTest(null);
        setPreviousSelectedTestId(null);
      }
      setPreviousSelectedPanelId(null);
      setSelectedPanel(null);
    } else if (type === "panel") {
      if (selectedPanel !== item) {
        handleAllData();
        setSelectedPanel(item);
        setPreviousSelectedPanelId(item.id);
      } else {
        setSelectedPanel(null);
        setPreviousSelectedPanelId(null);
      }
      setSelectedTest(null);
      setPreviousSelectedTestId(null);
    }
  }, [selectedTest, selectedPanel]);

  const handleAllData = useCallback(() => {
    const token = getAuthToken();
    if (!token) return;

    axios
      .get(`${APIURL}/api/v1/staff/master-test-group/?${TZ}`, {
        headers: {
          Authorization: 'Token ' + token,
        },
      })
      .then((res) => {
        if (res.status === 200 && res.data.status === 'success') {
          setAllData(res.data);
        } else {
          alert.error(res.data.message || 'Error fetching data.');
        }
      })
      .catch((err) => {
        alert.error('Error fetching data.');
      });
  }, [alert, getAuthToken]);

  const handleSuccess = useCallback(() => {
    setAddPanel(false);
    setAddTest(false);
    if (selectedCategory) {
      handleTestGroupData(selectedCategory);
    }
    handleAllData();
  }, [selectedCategory, handleTestGroupData, handleAllData]);

  // Filter tests and panels based on search and selected category
  const filteredTests = useMemo(() => {
    if (search && searchData && selectedCategory?.label) {
      // Show intersection of search results and selected group
      const groupData = getGroupsWithSearchData[selectedCategory.label];
      return groupData?.tests || [];
    } else if (data) {
      // Normal filtering when not searching
      return data.tests || [];
    }
    return [];
  }, [search, searchData, selectedCategory, getGroupsWithSearchData, data]);

  const filteredPanels = useMemo(() => {
    if (search && searchData && selectedCategory?.label) {
      // Show intersection of search results and selected group
      const groupData = getGroupsWithSearchData[selectedCategory.label];
      return groupData?.panels || [];
    } else if (data) {
      // Normal filtering when not searching
      return data.panels || [];
    }
    return [];
  }, [search, searchData, selectedCategory, getGroupsWithSearchData, data]);

  // Determine the title for the details section
  const detailsTitle = useMemo(() => {
    if (showTestGroupDetails && selectedCategory) {
      return `Test Group Details - ${selectedCategory.label}`;
    } else if (selectedTest) {
      return `Test Details - ${selectedTest.test_name}`;
    } else if (selectedPanel) {
      return `Panel Details - ${selectedPanel.panel_name}`;
    }
    return 'Details';
  }, [showTestGroupDetails, selectedCategory, selectedTest, selectedPanel]);

  // Helper function to determine what to show in details area
  const shouldShowDefaultMessage = !showTestGroupDetails && !selectedTest && !selectedPanel;

  return (
    <>
      <div className="left-side-buttons" style={{ flex: (selectedPanel || selectedTest || showTestGroupDetails) ? 1 : 2 }}>
        <div className="left-side-buttons-title">Test Groups</div>
        <div className="left-side-buttons" style={{ borderRadius: 0, border: "none" }}>
          {displayedTestGroups.map((category) => (
            <button
              key={category.value}
              className={`category-button ${selectedCategory?.value === category.value ? "active" : ""}`}
              onClick={() => handleCategoryClick(category)}
            >
              {category.label}
            </button>
          ))}
          {search && displayedTestGroups.length === 0 && (
            <div style={{ padding: '10px', color: 'red', textAlign: 'center' }}>
              No groups found for "{search}"
            </div>
          )}
        </div>
      </div>
      <div className="test-panel-column" style={{ flex: (selectedPanel || selectedTest || showTestGroupDetails) ? 2 : 5 }}>
        <div className="left-side-buttons-title">
          <span>Panels</span>
          <button className='add-btn' onClick={() => setAddPanel(!addPanel)} style={{ backgroundColor: 'rgb(164, 154, 215)'}}><FaPlus /> Panel</button>
        </div>
        <div className="panel-box">
          <div className="panel-widgets">
            {filteredPanels.length > 0 ? (
              filteredPanels.map((panel) => (
                <div
                  key={panel.id}
                  className={`panel-widget ${selectedPanel?.id === panel.id ? "active" : ""}`}
                  onClick={() => handleItemClick(panel, "panel")}
                >
                  <h3>{panel.panel_name}</h3>
                </div>
              ))
            ) : (
              <label style={{ color: "red" }}>
                {search ? "No panels found" : "No added panels!!"}
              </label>
            )}
          </div>
        </div>
        <div className="left-side-buttons-title" style={{ borderRadius: "0px" }}>
          <span>LabTest</span>
          <button className='add-btn' onClick={() => setAddTest(!addTest)} style={{ backgroundColor: 'rgb(164, 154, 215)'}}><FaPlus /> Test</button>
        </div>
        <div className="test-box">
          <div className="test-widgets">
            {filteredTests.length > 0 ? (
              filteredTests.map((test) => (
                <div
                  key={test.id}
                  className={`test-widget ${selectedTest?.id === test.id ? "active" : ""}`}
                  onClick={() => handleItemClick(test, "test")}
                >
                  <h3>{test.test_name}</h3>
                </div>
              ))
            ) : (
              <label style={{ color: "red" }}>
                {search ? "No tests found" : "No added tests!!"}
              </label>
            )}
          </div>
        </div>
      </div>
      <div className="pricing-details" style={{ flex: (selectedPanel || selectedTest || showTestGroupDetails) ? 3 : 2 }}>
        <div
          className="left-side-buttons-title"
          style={{ cursor: 'pointer' }}
        >
          {detailsTitle}
        </div>
        {showTestGroupDetails && selectedCategory ? (
          <TestDetails
            type='testgroup'
            selected={"testgroup"}
            selectedTest={data}
            selectedTestGroup={selectedCategory}
            data={allData}
            handleSuccess={handleSuccess}
            sample=""
          />
        ) : selectedTest ? (
          <TestDetails
            type='testgroup'
            selected={"test"}
            selectedTest={selectedTest}
            selectedTestGroup={selectedCategory}
            data={allData}
            handleSuccess={handleSuccess}
            sample={selectedTest.sample}
          />
        ) : selectedPanel ? (
          <TestDetails
            type='testgroup'
            selected={"panels"}
            selectedTest={selectedPanel}
            selectedTestGroup={selectedCategory}
            data={allData}
            handleSuccess={handleSuccess}
            sample={selectedPanel.sample}
          />
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <p onClick={handleTestGroupHeaderClick} style={{ color: 'blue', cursor: 'pointer' }}>Edit&nbsp;</p>
              <p> test group details</p>
            </div>
            <p>Or select a test or panel to view its details</p>
          </div>
        )}
      </div>
      {addPanel && (
        <PanelShow
          type='testgroup'
          testGroup={selectedCategory}
          handleSuccess={handleSuccess}
          onClose={() => setAddPanel(false)}
        />
      )}
      {addTest && (
        <TestShow
          type='testgroup'
          testGroup={selectedCategory}
          handleSuccess={handleSuccess}
          onClose={() => setAddTest(false)}
        />
      )}
    </>
  );
};

export default React.memo(TestGroup);